/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { InputAction, actionToDirection } from "../enums/InputAction";
import type { NavigationDirection } from "../enums/InputAction";
import { Log } from "../logging/Log";
import type { TInputEvent, InputEventType } from "./types/TInputEvent";
import type { ScreenInputHandler } from "./types/TScreenController";
import type { IScreenController } from "./interfaces/IScreenController";
import type { IModalController } from "./interfaces/IModalController";

/**
 * Base class for screen input controllers.
 *
 * Routing order:
 *   1. Modal stack (top → bottom) — top modal handles first, bubbles down
 *   2. Explicit screen-level handler (registered via registerHandler)
 *   3. Default handleNavigation (for NAV_UP/DOWN/LEFT/RIGHT)
 *
 * Subclasses MUST implement `initialize()` to register their handlers.
 * Override `handleNavigation()` to customize directional navigation behavior.
 */
export abstract class ScreenController implements IScreenController {
  protected static TAG: string = "ScreenController";

  public readonly screenName: string;

  private mHandlers: Map<string, ScreenInputHandler> = new Map();
  private mModalStack: IModalController[] = [];
  private mBoundaryHandlers: Map<
    NavigationDirection,
    (focusKey: string) => void
  > = new Map();

  constructor(screenName: string) {
    this.screenName = screenName;
    this.initialize();
  }

  protected abstract initialize(): void;

  // ────────────────────────────────────────────
  //  Handler Registration
  // ────────────────────────────────────────────

  protected registerHandler(
    action: InputAction,
    handler: ScreenInputHandler,
    eventType?: InputEventType | "all"
  ): void {
    const resolvedType =
      eventType !== null && eventType !== undefined
        ? eventType
        : this.defaultEventType(action);

    if (resolvedType === "all") {
      this.mHandlers.set(`${action}:*`, handler);
    } else {
      this.mHandlers.set(`${action}:${resolvedType}`, handler);
    }
  }

  private defaultEventType(action: InputAction): InputEventType {
    switch (action) {
      case InputAction.POINTER_CLICK:
        return "click";
      case InputAction.POINTER_CONTEXT_MENU:
        return "contextmenu";
      case InputAction.POINTER_HOVER:
        return "mouseenter";
      case InputAction.POINTER_LEAVE:
        return "mouseleave";
      case InputAction.SCROLL_UP:
      case InputAction.SCROLL_DOWN:
        return "wheel";
      default:
        return "keydown";
    }
  }

  // ────────────────────────────────────────────
  //  Default Navigation — Override in Subclass
  // ────────────────────────────────────────────

  /**
   * Default handler for NAV_UP / NAV_DOWN / NAV_LEFT / NAV_RIGHT.
   *
   * Override this method to provide spatial focus movement when
   * FocusRegistry is integrated. The default implementation is a
   * no-op that returns false (unhandled).
   *
   * Subclasses can override to:
   *   - Call FocusRegistry.moveFocus(direction) for spatial movement
   *   - Fetch data instead of moving focus ("fixed focus, data moves" pattern)
   *   - Perform manual scroll control
   */
  protected handleNavigation(_event: TInputEvent): boolean {
    return false;
  }

  // ────────────────────────────────────────────
  //  Boundary Navigation
  // ────────────────────────────────────────────

  protected registerBoundaryHandler(
    direction: NavigationDirection,
    handler: (focusKey: string) => void
  ): void {
    this.mBoundaryHandlers.set(direction, handler);
  }

  /**
   * Called when spatial navigation reaches a boundary (no element
   * found in the given direction). Override or register boundary
   * handlers to load more data, wrap around, etc.
   */
  protected handleBoundary(
    direction: NavigationDirection,
    currentFocusKey: string
  ): boolean {
    const handler = this.mBoundaryHandlers.get(direction);
    if (handler) {
      try {
        handler(currentFocusKey);
        return true;
      } catch (error: unknown) {
        Log.e(
          ScreenController.TAG,
          `handleBoundary() — error in screen "${this.screenName}" (${direction})`,
          error
        );
      }
    }
    return false;
  }

  // ────────────────────────────────────────────
  //  Modal Stack Management
  // ────────────────────────────────────────────

  public pushModal(modal: IModalController): void {
    const isDuplicate = this.mModalStack.some((m) => m.name === modal.name);
    if (isDuplicate) {
      Log.w(
        ScreenController.TAG,
        `pushModal() — modal "${modal.name}" already in stack on screen "${this.screenName}"`
      );
      return;
    }
    this.mModalStack.push(modal);
  }

  public popModal(expectedName?: string): IModalController | undefined {
    if (this.mModalStack.length === 0) {
      return undefined;
    }

    const top = this.mModalStack[this.mModalStack.length - 1];
    if (
      expectedName !== null &&
      expectedName !== undefined &&
      top.name !== expectedName
    ) {
      Log.w(
        ScreenController.TAG,
        `popModal() — expected "${expectedName}" but top is "${top.name}" on screen "${this.screenName}"`
      );
      return undefined;
    }

    return this.mModalStack.pop();
  }

  public removeModal(name: string): void {
    this.mModalStack = this.mModalStack.filter((m) => m.name !== name);
  }

  public getModalStackNames(): string[] {
    return this.mModalStack.map((m) => m.name);
  }

  public hasActiveModal(): boolean {
    return this.mModalStack.length > 0;
  }

  // ────────────────────────────────────────────
  //  Input Routing (Modal Stack → Explicit Handlers → Default Nav)
  // ────────────────────────────────────────────

  public handleInput(event: TInputEvent): boolean {
    // 1. Try modal stack (top → bottom)
    for (let i = this.mModalStack.length - 1; i >= 0; i--) {
      const modal = this.mModalStack[i];
      try {
        if (modal.handleInput(event)) {
          return true;
        }
      } catch (error: unknown) {
        Log.e(
          ScreenController.TAG,
          `handleInput() — error in modal "${modal.name}" on screen "${this.screenName}"`,
          error
        );
      }
    }

    // 2. Try explicit screen-level handler
    const exactKey = `${event.action}:${event.eventType}`;
    const wildcardKey = `${event.action}:*`;
    const exactHandler = this.mHandlers.get(exactKey);
    const handler =
      exactHandler === undefined
        ? this.mHandlers.get(wildcardKey)
        : exactHandler;

    if (handler) {
      try {
        return handler(event);
      } catch (error: unknown) {
        Log.e(
          ScreenController.TAG,
          `handleInput() — handler error for ${exactKey} on screen "${this.screenName}"`,
          error
        );
        return false;
      }
    }

    // 3. Fall back to default navigation for nav keys
    const direction = actionToDirection(event.action);
    if (direction !== null && event.eventType === "keydown") {
      return this.handleNavigation(event);
    }

    return false;
  }

  public cleanup(): void {
    for (const modal of this.mModalStack) {
      modal.cleanup();
    }
    this.mModalStack = [];
    this.mHandlers.clear();
    this.mBoundaryHandlers.clear();
  }
}
